import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { route } from '~/app/helpers/route';
import usePageTitle from '~/app/hooks/usePageTitle';
import { useAppHeader } from '~/app/modules/appHeader';
import { ROUTES } from '~/app/Router';
import Loader from '~/app/common/Loader';
import { useStoreActions, useStoreState } from '~/store';
import { WorkOrder } from '../types';
import MainContent from './MainContent';
import OperatingTime from './OperatingTime';
import Description from './Description';
import Tasks from './Tasks';
import Summary from './Summary';
import Comment from './Comment';
import Attachment from './Attachment';
import Administrative from './Administrative';
import History from './History';
import Footer from './Footer';
import classes from './style.module.scss';
import StatusMessage from './StatusMessage';
import classNames from 'classnames';
import Materials from './Material';
import Parts from './Parts';

export type DetailComponent = React.FC

const Detail: DetailComponent = () => {
    const { id } = useParams<{ id: WorkOrder['id'] }>();
    const [isFooterOpen, setIsFooterOpen] = useState(false);
    const { getWorkOrderDetail } = useStoreActions(state => state.workOrder);
    const { data } = useStoreState(state => state.workOrder);
    const fetching = useStoreState(state => state.workOrder.fetching);
    const netWorkStatus = useStoreState(state => state.app.offline.status);
    const projects = useStoreState(state => state.projects.list);

    const fetchWorkTypesAction = useStoreActions(actions => actions.issue.fetchWorkTypes);
    const fetchCustomersAction = useStoreActions(actions => actions.issue.fetchCustomers);
    const fetchProjectsAction = useStoreActions(actions => actions.projects.fetch);
    const fetchCostCategoriesAction = useStoreActions(actions => actions.issue.fetchCostCategories);

    const history = useHistory();
    const { setData } = useAppHeader();

    const pageTitle = useMemo(() => {
        return data.workOrderNo ? data.workOrderNo : 'Arbeidsordre';
    }, [data.workOrderNo]);

    useEffect(() => {
        getWorkOrderDetail({ id: Number(id), showLoading: true });
    }, [id, getWorkOrderDetail]);

    useEffect(() => {
        if (netWorkStatus === 0) {
            fetchWorkTypesAction();
            fetchCustomersAction();
            if (!projects?.length) {
                fetchProjectsAction();
            }
            if (data.departmentId) {
                fetchCostCategoriesAction(String(data.departmentId));
            }
        }
    }, [
        netWorkStatus,
        fetchWorkTypesAction,
        fetchCustomersAction,
        fetchProjectsAction,
        fetchCostCategoriesAction,
        data.departmentId,
        projects?.length,
    ]);

    usePageTitle(pageTitle);
    useEffect(() => {
        setData({
            title: pageTitle,
            subTitle: (data.excavator.intern + ' - ' + data.excavator.name).toUpperCase(),
            backLink: history.length > 1 ? history.goBack : route(ROUTES.workorders),
            showUser: false,
            machineId: '',
            projectId: '',
            showMachineMessages: false,
            isIssueDetailPage: false,
            notification: false,
            search: false,
        });
    }, [setData, history, data.excavator.intern, data.excavator.name, pageTitle]);

    if (fetching) {
        return <Loader size="32" />;
    }

    return (
        <>
            <div className={classNames(classes['page'], {[classes['footer-open']]: isFooterOpen})}>
                <StatusMessage status={data.status} statusInfo={data.statusInfo} />
                <MainContent data={data} />
                <OperatingTime
                    workOrderId={data.id}
                    status={data.status}
                    excavator={data.excavator}
                    operatingTime={data.operatingTime}
                    operatingCount={data.operatingCount}
                />
                <Description description={data.description} workOrderId={data.id} status={data.status} />
                <Tasks data={data} />
                <Parts />
                <Materials />
                <Summary summary={data.summary} />
                <Comment comment={data.comment} workOrderId={data.id} status={data.status} />
                <Attachment attachments={data.attachments} workOrderId={data.id} status={data.status} />
                <Administrative data={data} />
                <History history={data.history} createdBy={data.createdBy} />
            </div>
            <Footer data={data} isFooterOpen={isFooterOpen} setIsFooterOpen={setIsFooterOpen} />
        </>
    );
};

export default Detail;
